import React, { type ReactNode, useLayoutEffect, useRef, useCallback, forwardRef } from 'react';
import { styled } from '@compiled/react';
import Popup, { type PopupComponentProps, type TriggerProps } from '@atlaskit/popup'; // ignore-for-ENGHEALTH-17759
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { CONTEXT_MENU_ITEM_OVERFLOW_VISIBLE_CONTAINER_TESTID } from '../../../../../common/constants.tsx';
import type { CommonMenuItemProps } from '../../../../../common/types.tsx';
import {
	useShouldForceFocus,
	useContextMenuActions,
} from '../../../../../controllers/context-menu/index.tsx';
import { ContextMenuItemButton } from './context-menu-item-button/index.tsx';
import { SubmenuContainer } from './submenu-container/index.tsx';
import { useSubmenuPersistentFocus } from './utils.tsx';

type Props = {
	scope: string;
	children?: ReactNode;
	shouldRenderToParent?: boolean;
} & CommonMenuItemProps;

const OverflowVisiblePopupContainer = forwardRef<HTMLDivElement, PopupComponentProps>(
	({ children, ...props }, ref) => (
		<OverflowVisiblePopupContainerStyled
			{...props}
			ref={ref}
			data-testid={CONTEXT_MENU_ITEM_OVERFLOW_VISIBLE_CONTAINER_TESTID}
		>
			{children}
		</OverflowVisiblePopupContainerStyled>
	),
);

export const ContextMenuItem = ({
	scope,
	menuItem,
	children,
	onMouseEnter,
	shouldRenderToParent = true,
	...others
}: Props) => {
	const ref = useRef<HTMLButtonElement | null>(null);
	const { setForcedFocusPath } = useContextMenuActions();
	const shouldFocus = useShouldForceFocus({ scope, menuItem });
	const { persistentFocus, enablePersistentFocus, disablePersistentFocus, isOpen } =
		useSubmenuPersistentFocus({ menuItem, scope });

	useLayoutEffect(() => {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		if (ref.current !== null && document.activeElement !== ref.current) {
			if (shouldFocus) {
				ref.current?.focus();
				setForcedFocusPath(scope, undefined);
			}
		}
	}, [shouldFocus, scope, setForcedFocusPath]);

	const Content = useCallback(
		() => (
			<SubmenuContainer
				width={menuItem.submenuWidth}
				shouldOverflowChildren={menuItem.shouldOverflowChildren}
				persistentFocus={persistentFocus}
			>
				{children}
			</SubmenuContainer>
		),
		[menuItem.submenuWidth, menuItem.shouldOverflowChildren, children, persistentFocus],
	);

	return (
		<Popup
			autoFocus={false}
			offset={[0, 0]}
			isOpen={!!isOpen && !!children}
			popupComponent={menuItem.shouldOverflowChildren ? OverflowVisiblePopupContainer : undefined}
			content={Content}
			placement="right-start"
			shouldRenderToParent={shouldRenderToParent}
			trigger={({ ref: popupRef, ...popupProps }: TriggerProps) => (
				<ContextMenuItemButton
					{...popupProps}
					ref={(r: HTMLButtonElement) => {
						if (popupRef) {
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							(popupRef as React.RefCallback<HTMLButtonElement | null>)(r);
						}
						ref.current = r;
					}}
					isOpen={isOpen}
					hasChildren={!!children}
					menuItem={menuItem}
					onOpenWithKeyboard={enablePersistentFocus}
					onMouseEnter={(node) => {
						disablePersistentFocus();
						onMouseEnter(node);
					}}
					{...others}
				/>
			)}
		/>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OverflowVisiblePopupContainerStyled = styled.div({
	overflow: 'visible',
	borderRadius: 3,
	boxShadow: token(
		'elevation.shadow.overlay',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		`0 4px 8px -2px ${colors.N50A}, 0 0 1px ${colors.N60A}`,
	),
});
